import { useQuery } from "@tanstack/react-query";
import { api } from "../api";

export function useGetMetrics(userId: string, filters: any) {
  return useQuery({
    queryKey: ["metrics", userId, filters],
    queryFn: () => api.getMetrics(userId, filters),
    enabled: !!userId && !!filters,
  });
}

/**
 * Per-KPI time-series for the dashboard KPI sparklines.
 * Backend bucketing: daily for ≤45-day ranges, monthly otherwise.
 * Returns 4 arrays — one per KPI card — that drive the trailing sparkline path.
 */
export interface KpiSparklinePoint {
  date: string;
  /** Current-period value at this bucket (drives the solid foreground line). */
  value: number;
  /**
   * Same Time Last Year value at this bucket — backend uses isSTLY logic so
   * future buckets only count what was on-the-books at this same moment a
   * year ago. Drives the dashed background line so the visual gap between
   * the two lines equals the pacing % shown in the KPI pill.
   */
  stlyValue?: number;
}

export interface KpiSparklinesResponse {
  granularity: "day" | "month";
  revenue: KpiSparklinePoint[];
  occupancy: KpiSparklinePoint[];
  adr: KpiSparklinePoint[];
  revpar: KpiSparklinePoint[];
}

export function useKpiSparklines(
  userId: string | undefined,
  startDate: string | undefined,
  endDate: string | undefined,
) {
  return useQuery<KpiSparklinesResponse>({
    queryKey: ["kpi-sparklines", userId, startDate, endDate],
    queryFn: () =>
      api.getMetrics(userId as string, {
        type: "KpiSparklines",
        startDate: startDate as string,
        endDate: endDate as string,
      }) as Promise<KpiSparklinesResponse>,
    enabled: !!userId && !!startDate && !!endDate,
    staleTime: 5 * 60 * 1000,
  });
}
